// ============================== [業務 API 服務層] ==============================
// 專注於業務邏輯，使用底層的 apiClient 提供的 axios 配置和認證

import { createApiClient, clearAuth, isAuthenticated, getAuthConfig, getTemporaryAccessToken, AuthConfig } from './apiClient';

// ============================== [測試環境配置] ==============================

// 測試環境帳密登入配置
const createTestPasswordConfig = (username: string, password: string, baseUrl: string): AuthConfig => {
  return {
    mode: 'password',
    endpoints: {
      login: '/auth/login',  // 登錄端點 (相對路徑)
    },
    credentials: {
      username,
      password
    },
    serverUrl: baseUrl, // 基礎服務器 URL
  };
};

// 測試環境專用 osApi 創建函數
export const createTestOsApi = (username: string, password: string, baseUrl: string) => {
  const testAuthConfig = createTestPasswordConfig(username, password, baseUrl);
  return createOsApi(testAuthConfig);
};

// ============================== [工具函數] ==============================

// 將 blob url 轉換為 blob
const changeBlobUrl2Blob = async (blobUrl: string) => {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('changeBlobUrl2Blob error:', error);
    return null;
  }
};

// ============================== [配置化 API 工廠函數] ==============================

// 創建配置化的 osApi 實例
export const createOsApi = (authConfig?: AuthConfig) => {
  // 創建配置化的 client 獲取函數
  const getClient = () => createApiClient(authConfig);

  return {
    // ============================== [認證相關] ==============================
    getAuthConfig: () => getAuthConfig(authConfig),  // 使用指定配置
    getTemporaryAccessToken,          // 備用認證方案
    clearAuth,                        // 清除認證狀態
    isAuthenticated,                  // 檢查認證狀態

    // ============================== [群組操作] ==============================
    // [GET] 群組列表或單一群組詳情
    getGroup: async (group_id?: string) => {
      try {
        const client = await getClient();
        const url = group_id ? `/group/${group_id}` : '/group/';
        return await client.get(url);
      } catch (error) {
        throw error;
      }
    },

    // [GET] 根據名稱查詢群組
    getGroupByName: async (name: string) => {
      try {
        const client = await getClient();
        return await client.get(`/group/byName?name=${name}`);
      } catch (error) {
        throw error;
      }
    },

    // [GET] 根據名稱獲取群組詳情
    getGroupDetailsByName: async (name: string) => {
      try {
        const client = await getClient();
        return await client.get(`/group/details/byName?name=${name}`);
      } catch (error) {
        throw error;
      }
    },

    // [POST] 創建群組
    createGroup: async ({ name, description }: { name: string; description: string }) => {
      try {
        const client = await getClient();
        return await client.post(`/group/?name=${name}&description=${description}`, {});
      } catch (error) {
        throw error;
      }
    },

    // [POST] 創建群組項目
    createGroupItem: async ({ group_id, data }: { group_id: string; data: any }) => {
      try {
        const client = await getClient();
        return await client.post(`/group/${group_id}`, data);
      } catch (error) {
        throw error;
      }
    },

    // ============================== [項目操作] ==============================
    // [GET] 根據 ID 獲取項目
    getItemById: async (item_id: string) => {
      try {
        const client = await getClient();
        return await client.get(`/item/${item_id}`);
      } catch (error) {
        throw error;
      }
    },

    // [POST] 上傳檔案到項目 (formData)
    uploadFileToItem: async ({ item_id, formData }: { item_id: string; formData: FormData }) => {
      try {
        const client = await getClient();
        const config = {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        };
        return await client.post(`/item/${item_id}`, formData, config);
      } catch (error) {
        throw error;
      }
    },

    // [POST] 上傳檔案到項目 (files)
    uploadBlobToItem: async ({ item_id, file }: { item_id: string; file: any }) => {
      try {
        const client = await getClient();
        const formData = new FormData();

        if (file?.type === 'application/json') {
          formData.append('file', file, `item-${Date.now()}.json`);
        } else {
          const blob = await changeBlobUrl2Blob(file.url);
          if (blob) {
            formData.append('file', blob, file.name);
          }
        }

        const config = {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        };

        return await client.post(`/item/${item_id}`, formData, config);
      } catch (error) {
        throw error;
      }
    },

    // [PATCH] 更新項目資訊
    updateItemInformation: async ({ item_id, data }: { item_id: string; data: any }) => {
      try {
        const client = await getClient();
        return await client.patch(`/item/${item_id}/information`, data);
      } catch (error) {
        throw error;
      }
    },

    // [PATCH] 更新項目名稱
    updateItemName: async ({ item_id, name }: { item_id: string; name: string }) => {
      try {
        const client = await getClient();
        return await client.patch(`/item/${item_id}/name?name=${name}`);
      } catch (error) {
        throw error;
      }
    },

    // [DELETE] 刪除項目
    deleteItem: async ({ item_id }: { item_id: string }) => {
      try {
        const client = await getClient();
        return await client.delete(`/item/${item_id}`);
      } catch (error) {
        throw error;
      }
    },

    // [DELETE] 刪除項目檔案
    deleteItemBlob: async ({ item_id, blob_id }: { item_id: string; blob_id: string }) => {
      try {
        const client = await getClient();
        return await client.delete(`/item/${item_id}/blobs/${blob_id}`);
      } catch (error) {
        throw error;
      }
    },
  };
};

// ============================== [默認 API 實例] ==============================
// 向後兼容：使用默認認證配置
export const osApi = createOsApi(); // 等同於之前的 osApi，但使用工廠函數創建

// export const osApi = createTestOsApi(
//   'healtech.ai.dev',
//   'healtech.ai.dev',
//   'https://os.api.scfg.io'
// );